import React, { useRef, useState, useEffect, useContext } from 'react';
import { useTranslation } from "react-i18next";
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Tooltip } from 'primereact/tooltip';
import { Button } from 'primereact/button';
import Loading from '../../../Loading/loading';
import '../settings.css';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import CustomDropdown from '../../../custom/customDropdown';
import { Dialog } from 'primereact/dialog';
import { AuthContext } from '../../../Header/auth - context';
import { ChatSocketContext } from '../../../context/ChatSocketContext';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { MapContainer, TileLayer, Marker, Circle, Tooltip as MapTooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
/* Podloge su ISTE kao na GPS mapi — jedan spisak, jedno mesto za izmenu. Kad se
   tamo doda ili zameni podloga, ovaj dijalog je dobija bez ijedne izmene. */
import { SLOJEVI, podrazumevaniSloj } from '../../GPS/slojeviMape';

/* Oznake se sklapaju kao `divIcon` sa ugrađenim SVG-om — isti obrazac kao GPS
   (`IKONA_PIN`). Razlog je što podrazumevani Leaflet pin ne može da se razlikuje
   po značenju: ovde se MORA videti šta je staro a šta je javljeno sada.

   Poslednja poznata lokacija je mirna i siva: nju niko nije tražio, ona samo
   stoji. Živo javljanje je u akcentnoj boji i pulsira — to je ono što je dispečer
   upravo zatražio i što čeka da se pojavi. */
const IKONA_POSLEDNJA = L.divIcon({
  className: 'mob-pin mob-pin-stara',
  html: '<svg viewBox="0 0 24 34" width="26" height="36" aria-hidden="true">'
      + '<path d="M12 0.9C6.2 0.9 1.5 5.6 1.5 11.4c0 7.6 9 20.2 9.4 20.8a1.4 1.4 0 0 0 2.2 0c.4-.6 9.4-13.2 9.4-20.8C22.5 5.6 17.8.9 12 .9z"/>'
      + '<circle cx="12" cy="11.4" r="4.1"/></svg>',
  iconSize: [26, 36],
  iconAnchor: [13, 36],
});

const IKONA_ZIVO = L.divIcon({
  className: 'mob-pin mob-pin-zivo',
  html: '<span class="mob-pin-puls"></span>'
      + '<svg viewBox="0 0 24 34" width="30" height="42" aria-hidden="true">'
      + '<path d="M12 0.9C6.2 0.9 1.5 5.6 1.5 11.4c0 7.6 9 20.2 9.4 20.8a1.4 1.4 0 0 0 2.2 0c.4-.6 9.4-13.2 9.4-20.8C22.5 5.6 17.8.9 12 .9z"/>'
      + '<circle cx="12" cy="11.4" r="4.1"/></svg>',
  iconSize: [30, 42],
  iconAnchor: [15, 42],
});

// Kad se markeri promene (stigne živo javljanje), mapa se sama namesti da ih obuhvati.
const UokviriMarkere = ({ tacke }) => {
  const map = useMap();
  useEffect(() => {
    if (!tacke.length) return;
    if (tacke.length === 1) { map.setView(tacke[0], 14); return; }
    map.fitBounds(L.latLngBounds(tacke), { padding: [30, 30] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tacke.map(t => t.join(',')).join('|')]);
  return null;
};

const emptyForm = { id: null, id_zaposleni: null, rola: null, username: '', pin: '' };

// Prevodi za role — ključevi po auth.User_roles.id. Sama LISTA dostupnih rola dolazi sa
// backend-a (auth.User_roles), ovde je samo mapiranje id → i18n ključ; ako se u bazi pojavi
// nova mobilna rola bez prevoda, pada se nazad na naziv iz baze (vidi rolaLabel ispod).
const ROLA_KEYS = { 1: 'admin.rolaDirektorKontrolor', 5: 'admin.rolaDispecer', 6: 'admin.rolaVozac', 8: 'admin.rolaServiser' };

// Predlog username-a iz imena/prezimena, bez dijakritika (ime.prezime).
const DIACRITICS_RE = /[̀-ͯ]/g;
const suggestUsername = (firstName, lastName) => {
  const strip = (s) => (s || '')
    .normalize('NFD').replace(DIACRITICS_RE, '')
    .replace(/[^a-zA-Z]/g, '')
    .toLowerCase();
  return [strip(firstName), strip(lastName)].filter(Boolean).join('.');
};

const MobilniOperateriTab = () => {
  const { t } = useTranslation();
  const toast = useRef(null);
  const auth  = useContext(AuthContext);
  const { socket } = useContext(ChatSocketContext) || {};

  const [role, setRole] = useState([]); // iz auth.User_roles (backend), ne hardkodovana lista

  // Prevod ako postoji ključ za taj id; inače naziv iz baze (auth.User_roles.role)
  const rolaLabel = (rola) => {
    if (ROLA_KEYS[rola]) return t(ROLA_KEYS[rola]);
    const r = role.find(x => x.id === rola);
    return r ? r.role : '-';
  };
  const rolaOptions = role.map(r => ({ value: r.id, label: rolaLabel(r.id) }));

  const [load, setLoad]     = useState(true);
  const [nalozi, setNalozi] = useState([]);
  const [zaposleni, setZaposleni] = useState([]);
  const [render, setRender] = useState(false);

  const [open, setOpen]         = useState(false);
  const [selected, setSelected] = useState(null); // nalog red kad se edituje, null = kreiranje
  const [formData, setFormData] = useState(emptyForm);
  const [showPin, setShowPin]   = useState(false);
  const [saving, setSaving]     = useState(false);
  // Koji redovi u tabeli trenutno pokazuju PIN (otvoreno oko). Po redu, ne globalno —
  // da jedan klik ne otkrije PIN-ove svih vozača nekome ko gleda preko ramena.
  const [vidljivPin, setVidljivPin] = useState(() => new Set());
  const prebaciPin = (id) => setVidljivPin((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const [globalFilter, setGlobalFilter] = useState(null);
  const [first, setFirst] = useState(0);

  // PRONAĐI VOZAČA (Deo F)
  const [pronadjiOpen, setPronadjiOpen] = useState(false);
  const [pronadjiRow, setPronadjiRow] = useState(null);
  const [pronadjiRequestId, setPronadjiRequestId] = useState(null);
  const [pronadjiPoslednja, setPronadjiPoslednja] = useState(null); // {lat,lng,gps_enabled,tacnost_m,DateEntered} sa backend-a (odmah pri triger-u)
  const [pronadjiOdgovor, setPronadjiOdgovor] = useState(null);     // live odgovor sa telefona, stiže preko socket-a
  const [pronadjiCekanje, setPronadjiCekanje] = useState(false);
  const [pronadjiLoading, setPronadjiLoading] = useState(false);
  /* Ishod slanja push poruke ({poslat, greska}) — backend ga vraća odmah. Bez ovoga
     se vrtelo „Čeka se odgovor telefona…" i kad poruka uopšte nije krenula, pa je
     dispečer krivio vozača za grešku koja je bila na strani servisa. */
  const [pronadjiPush, setPronadjiPush] = useState(null);
  /* Istek čekanja. Push ima TTL od 5 minuta; posle toga nema šta da se čeka, a
     spiner koji se vrti u nedogled laže da se nešto i dalje dešava. */
  const [pronadjiIsteklo, setPronadjiIsteklo] = useState(false);
  /* Dijagnostika sa servera (stanje naloga, poslednja aktivnost aplikacije, istorija
     odgovora), potvrda isporuke od Expo-a i znak da se čeka duže nego što telefon
     inače odgovara (par sekundi) — da dispečer zna ZAŠTO nema odgovora. */
  const [pronadjiDijag, setPronadjiDijag] = useState(null);
  const [pronadjiPotvrda, setPronadjiPotvrda] = useState(null);   // { stanje, greska }
  const [pronadjiDugo, setPronadjiDugo] = useState(false);
  /* Izabrana podloga mape. Pamti se u stanju a ne u ref-u jer promena MORA da
     ponovo iscrta TileLayer. Podrazumevana je ista kao na GPS-u. */
  const [sloj, setSloj] = useState(podrazumevaniSloj);

  const renderFunction = () => setRender(prev => !prev);

  const handleErrors = (response) => {
    if (response.ok) return response.json();
    throw response;
  };

  const showError = (error) => {
    if (error && error.json) {
      error.json().then(e => toast.current.show({ severity: 'error', summary: 'Error', detail: e.message, life: 3000 }));
    } else {
      toast.current.show({ severity: 'error', summary: 'Error', detail: t('admin.m_greska_na_serveru'), life: 3000 });
    }
  };

  useEffect(() => {
    fetch(process.env.REACT_APP_BACKEND_URL + '/mobile-operators/config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({}),
    })
      .then(handleErrors)
      .then(result => {
        setNalozi(result.nalozi || []);
        setZaposleni(result.zaposleni || []);
        setRole(result.role || []);
        setLoad(false);
      })
      .catch(error => { showError(error); setLoad(false); });
  }, [render]); // eslint-disable-line

  const employeeOptionsForRola = (rola) => zaposleni
    .filter(z => !rola || String(z.position) === String(rola))
    .map(z => ({
      label: `${z.FirstName} ${z.LastName}${z.MobileNumber ? ' — ' + z.MobileNumber : ''}`,
      value: z.id_zaposleni,
      FirstName: z.FirstName,
      LastName: z.LastName,
    }));

  const handleOpenCreate = () => {
    setSelected(null);
    setFormData(emptyForm);
    setShowPin(false);
    setOpen(true);
  };

  const handleSelectNalog = (row) => {
    setSelected(row);
    setFormData({ id: row.id, id_zaposleni: row.id_zaposleni, rola: row.rola, username: row.username, pin: '' });
    setShowPin(false);
    setOpen(true);
  };

  const handleCloseWindow = () => { setOpen(false); setSelected(null); setFormData(emptyForm); };

  const handleRolaChange = (rola) => {
    setFormData(prev => ({ ...prev, rola, id_zaposleni: null }));
  };

  const handleEmployeeChange = (id_zaposleni) => {
    const options = employeeOptionsForRola(formData.rola);
    const emp = options.find(o => String(o.value) === String(id_zaposleni));
    setFormData(prev => ({
      ...prev,
      id_zaposleni: emp ? emp.value : id_zaposleni,
      username: prev.username || (emp ? suggestUsername(emp.FirstName, emp.LastName) : ''),
    }));
  };

  const handleSave = () => {
    if (!formData.id_zaposleni || !formData.rola || !formData.username) {
      toast.current.show({ severity: 'warn', summary: t('admin.m_upozorenje'), detail: t('admin.m_popuni_sva_obavezna_polja'), life: 3000 });
      return;
    }
    if (!selected && !formData.pin) {
      toast.current.show({ severity: 'warn', summary: t('admin.m_upozorenje'), detail: t('admin.mobOpPinObavezan'), life: 3000 });
      return;
    }
    if (formData.pin && !/^\d{4}$/.test(formData.pin)) {
      toast.current.show({ severity: 'warn', summary: t('admin.m_upozorenje'), detail: t('admin.mobOpPinFormat'), life: 3000 });
      return;
    }
    setSaving(true);
    fetch(process.env.REACT_APP_BACKEND_URL + '/mobile-operators', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({
        id: formData.id,
        id_zaposleni: formData.id_zaposleni,
        rola: formData.rola,
        username: formData.username,
        pin: formData.pin || undefined,
      }),
    })
      .then(handleErrors)
      .then(data => {
        toast.current.show({ severity: 'success', summary: t('admin.m_uspesno'), detail: data.message, life: 3000 });
        handleCloseWindow();
        renderFunction();
      })
      .catch(showError)
      .finally(() => setSaving(false));
  };

  const handleToggleStatus = (row, newStatus) => {
    fetch(process.env.REACT_APP_BACKEND_URL + '/mobile-operators/status', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({ id: row.id, status: newStatus ? 1 : 0 }),
    })
      .then(handleErrors)
      .then(data => { toast.current.show({ severity: 'success', summary: t('admin.m_uspesno'), detail: data.message, life: 3000 }); renderFunction(); })
      .catch(showError);
  };


  // PRONAĐI VOZAČA — isti socket koji Chat već koristi (ne otvara se nova konekcija)
  useEffect(() => {
    // Zavisi od socket STANJA, ne od ref-a — vidi ChatSocketContext.js.
    if (!socket) return;
    const onOdgovor = (payload) => {
      setPronadjiRequestId((current) => {
        if (payload.requestId !== current) return current;
        setPronadjiOdgovor(payload);
        setPronadjiCekanje(false);
        return current;
      });
    };
    socket.on('pronadji-vozaca-odgovor', onOdgovor);
    return () => socket.off('pronadji-vozaca-odgovor', onOdgovor);
  }, [socket]);

  const handlePronadjiVozaca = (row) => {
    setPronadjiRow(row);
    setPronadjiPoslednja(null);
    setPronadjiOdgovor(null);
    setPronadjiRequestId(null);
    setPronadjiPush(null);
    setPronadjiIsteklo(false);
    setPronadjiDijag(null);
    setPronadjiPotvrda(null);
    setPronadjiDugo(false);
    setPronadjiOpen(true);
    setPronadjiLoading(true);
    fetch(process.env.REACT_APP_BACKEND_URL + '/mobile-operators/pronadjiVozaca', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
      body: JSON.stringify({ id: row.id }),
    })
      .then(handleErrors)
      .then((data) => {
        setPronadjiRequestId(data.requestId);
        setPronadjiPoslednja(data.poslednja || null);
        /* Stariji backend ne vraća `push` — tada se ponašamo kao pre i čekamo. */
        const push = data.push || { poslat: true };
        setPronadjiPush(push);
        setPronadjiCekanje(!!push.poslat);
        setPronadjiDijag(data.dijagnostika || null);
      })
      .catch((error) => { showError(error); setPronadjiOpen(false); })
      .finally(() => setPronadjiLoading(false));
  };

  /* Push ima TTL od 5 minuta (vidi `pronadjiVozaca` na backendu). Posle toga poruka
     više neće biti isporučena, pa nema šta da se čeka — spiner koji se vrti u
     nedogled laže da se nešto i dalje dešava. */
  const TTL_PUSH_MS = 300000;
  useEffect(() => {
    if (!pronadjiCekanje) return undefined;
    const tajmer = setTimeout(() => {
      setPronadjiCekanje(false);
      setPronadjiIsteklo(true);
    }, TTL_PUSH_MS);
    return () => clearTimeout(tajmer);
  }, [pronadjiCekanje, pronadjiRequestId]);

  /* Telefon koji radi odgovori za 2–3 sekunde. Posle 20 s pita se Expo da li je
     poruku uopšte predao Google-u/Apple-u (potvrda postoji tek par sekundi posle
     slanja, pa se još jednom pita na 60 s). Na 60 s se otvara spisak šta proveriti
     na telefonu — čekanje i dalje teče do isteka push-a (5 min). */
  useEffect(() => {
    if (!pronadjiCekanje) return undefined;
    const ticketId = pronadjiPush?.ticketId;
    const pitaj = () => {
      if (!ticketId) return;
      fetch(process.env.REACT_APP_BACKEND_URL + '/mobile-operators/pronadjiPotvrda', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
        body: JSON.stringify({ ticketId, id: pronadjiRow?.id }),
      })
        .then(r => (r.ok ? r.json() : null))
        .then(d => { if (d) setPronadjiPotvrda(prev => (prev?.stanje === 'greska' ? prev : d)); })
        .catch(() => {});
    };
    const t20 = setTimeout(pitaj, 20000);
    const t60 = setTimeout(() => { setPronadjiDugo(true); pitaj(); }, 60000);
    return () => { clearTimeout(t20); clearTimeout(t60); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pronadjiCekanje, pronadjiRequestId]);

  /* Kada je potvrda javila grešku, čekanje nema smisla — poruka nikad neće stići. */
  useEffect(() => {
    if (pronadjiPotvrda?.stanje === 'greska') setPronadjiCekanje(false);
  }, [pronadjiPotvrda]);

  const handlePronadjiClose = () => {
    setPronadjiOpen(false);
    setPronadjiRow(null);
    setPronadjiRequestId(null);
    setPronadjiPoslednja(null);
    setPronadjiOdgovor(null);
    setPronadjiCekanje(false);
  };

  const mapsLink = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;

  /* Razlog odbijanja push-a, ljudskim jezikom. Nepoznat kod se prikazuje kakav
     jeste — bolje sirov kod nego ništa, po njemu se traži u Expo dokumentaciji. */
  const razlogPusha = (greska) => ({
    DeviceNotRegistered: t('admin.mobOpTokenNevazi'),
    MessageRateExceeded: t('admin.mobOpPushPrecesto'),
    InvalidCredentials:  t('admin.mobOpPushKonfiguracija'),
  }[greska] || greska || '');

  const formatVreme = (v) => v ? new Date(v).toLocaleString('sr-RS', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade',
  }).replace(',', '') : '-';

  const exportExcelMobileOperators = async () => {
    if (!nalozi.length) return;
    const workbook  = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('MobilniOperateri');
    const headers   = ['Rb', 'Ime', 'Prezime', 'Rola', 'Mobilni broj', 'Username', 'Status', 'Uređaj upareno'];
    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.eachCell(cell => {
      cell.fill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
      cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
    });
    nalozi.forEach((n, i) => {
      const row = worksheet.addRow([
        i + 1, n.FirstName, n.LastName, rolaLabel(n.rola), n.MobileNumber || '', n.username,
        n.status == 1 ? 'Aktivan' : 'Blokiran', n.upareno == 1 ? 'Da' : 'Ne',
      ]);
      row.eachCell(cell => { cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }; });
    });
    worksheet.columns.forEach(col => { col.width = 22; });
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'MobilniOperateri.xlsx');
  };

  const nalogHeader = (
    <div className="export-buttons fakture-stavke-header">
      <div className="fakture-stavke-radnje">
        <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcelMobileOperators} data-pr-tooltip="XLS" />
        <Button type="button" icon="pi pi-plus" severity="info" rounded onClick={handleOpenCreate} data-pr-tooltip={t('admin.mobOpNoviNalog')} disabled={zaposleni.length === 0} />
      </div>
      <input
        type="search"
        onInput={(e) => setGlobalFilter(e.target.value)}
        placeholder={t('nova.phPretraga')}
        className="p-inputtext p-component"
      />
    </div>
  );

  return (
    <div id="setting_main">
      {load && <Loading />}
      <Toast ref={toast} />

      <div className="setting_table">
        <Card>
          <Tooltip target=".export-buttons>button" position="bottom" />
          <DataTable className="stavke-tabela"
            value={nalozi}
            paginator
            rows={10}
            first={first}
            onPage={(e) => setFirst(e.first)}
            globalFilter={globalFilter}
            scrollable
            scrollHeight="400px"
            header={nalogHeader}
            emptyMessage={t('admin.mobOpNemaNaloga')}
            resizableColumns
            columnResizeMode="expand"
            rowClassName={(row) => selected && row.id === selected.id ? 'selected-row' : ''}
          >
            <Column header={t('admin.rb')} body={(_, opts) => opts.rowIndex + 1} style={{ minWidth: '60px' }} />
            <Column field="FirstName" header={t('login.ime')} sortable style={{ minWidth: '100px' }} />
            <Column field="LastName" header={t('login.prezime')} sortable style={{ minWidth: '100px' }} />
            <Column header={t('admin.mobOpRola')} body={(row) => rolaLabel(row.rola)} sortable sortField="rola" style={{ minWidth: '150px' }} />
            <Column field="MobileNumber" header={t('admin.mobOpMobilni')} style={{ minWidth: '140px' }} />
            <Column field="username" header={t('admin.mobOpUsername')} sortable style={{ minWidth: '140px' }} />
            <Column
              header={t('admin.mobOpPin')}
              style={{ minWidth: '120px' }}
              body={(row) => (!row.pin ? (
                <span title={t('admin.mobOpPinNedostupan')}>—</span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <span style={{ fontFamily: 'monospace', letterSpacing: '0.1em' }}>
                    {vidljivPin.has(row.id) ? row.pin : '••••'}
                  </span>
                  <Button
                    type="button"
                    icon={`pi ${vidljivPin.has(row.id) ? 'pi-eye-slash' : 'pi-eye'}`}
                    className="p-button-text p-button-sm"
                    onClick={() => prebaciPin(row.id)}
                  />
                </span>
              ))}
            />
            <Column
              header={t('nova.status')}
              body={(row) => (
                <span style={{ color: row.status == 1 ? 'green' : 'red', fontWeight: 600 }}>
                  {row.status == 1 ? t('admin.mobOpAktivan') : t('admin.mobOpBlokiran')}
                </span>
              )}
              style={{ minWidth: '110px' }}
            />
            <Column
              header={t('admin.mobOpUredjajUpareno')}
              body={(row) => (row.upareno == 1 ? t('common.da') : t('common.ne'))}
              style={{ minWidth: '110px' }}
            />
            <Column
              header={t('admin.mobOpPronadji')}
              body={(row) => (
                row.rola === 6 && row.status == 1 && row.upareno == 1 ? (
                  <Button
                    icon="pi pi-map-marker"
                    type="button"
                    className="p-button-text p-button-sm"
                    severity="warning"
                    data-pr-tooltip={t('admin.mobOpPronadji')}
                    onClick={() => handlePronadjiVozaca(row)}
                  />
                ) : null
              )}
              style={{ minWidth: '80px' }}
            />
            <Column
              header={t('nova.otvori')}
              body={(row) => (
                <Button
                  icon="pi pi-pencil"
                  type="button"
                  className="p-button-text p-button-sm"
                  onClick={() => handleSelectNalog(row)}
                />
              )}
              style={{ minWidth: '80px' }}
            />
          </DataTable>
        </Card>
      </div>

      <div id="setting_second">
        <Dialog
          visible={open}
          onHide={handleCloseWindow}
          header={selected ? `${selected.FirstName} ${selected.LastName}` : t('admin.mobOpNoviNalog')}
          style={{ width: '520px' }}
          breakpoints={{ '768px': '95vw' }}
          modal
          draggable={false}
          resizable={false}
          footer={
            <div className="fakture-stavke-radnje">
              <div>
                {/* Otparivanje je izbačeno: uparivanje prati prijavu, pa se
                    telefon menja sam od sebe — prvom prijavom sa novog. */}
              </div>
              <div className="fakture-stavke-radnje">
                <Button onClick={handleSave} loading={saving} label={t('admin.sacuvaj')} className="p-button p-button-raised p-button-success" />
              </div>
            </div>
          }
        >
          <div className="card-body">
            <div style={{ margin: '1%', display: 'flex', width: '100%', alignItems: 'center' }}>
              <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', margin: '0 5% 0 0', width: '150px' }}>{t('admin.mobOpRola')}:</p>
              {selected ? (
                <span>{rolaLabel(selected.rola)}</span>
              ) : (
                <Dropdown
                  optionLabel="label"
                  value={formData.rola}
                  options={rolaOptions}
                  onChange={(e) => handleRolaChange(e.value)}
                  placeholder={t('admin.mobOpIzaberiRolu')}
                  style={{ flex: 1 }}
                />
              )}
            </div>
            <div style={{ margin: '1%', display: 'flex', width: '100%', alignItems: 'center' }}>
              <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', margin: '0 5% 0 0', width: '150px' }}>{t('admin.mobOpIzaberiZaposlenog')}:</p>
              {selected ? (
                <span>{selected.FirstName} {selected.LastName}</span>
              ) : (
                <CustomDropdown
                  optionLabel="label"
                  value={formData.id_zaposleni}
                  options={employeeOptionsForRola(formData.rola)}
                  onChange={(e) => handleEmployeeChange(e.target.value)}
                  placeholder={t('admin.mobOpIzaberiZaposlenog')}
                  filterPlaceholder={t('common.pretraga')}
                  emptyMessage={t('admin.mobOpNemaSlobodnihZaposlenih')}
                  disabled={!formData.rola}
                  style={{ flex: 1 }}
                />
              )}
            </div>
            <div style={{ margin: '1%', display: 'flex', width: '100%', alignItems: 'center' }}>
              <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', margin: '0 5% 0 0', width: '150px' }}>{t('admin.mobOpUsername')}:</p>
              <InputText
                value={formData.username}
                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                placeholder={t('admin.mobOpUsername')}
                style={{ flex: 1 }}
              />
            </div>
            <div style={{ margin: '1%', display: 'flex', width: '100%', alignItems: 'center' }}>
              <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', margin: '0 5% 0 0', width: '150px' }}>
                {selected ? t('admin.mobOpResetPin') : t('admin.mobOpPin')}:
              </p>
              <div style={{ display: 'flex', flex: 1, gap: '0.4rem' }}>
                <InputText
                  type={showPin ? 'text' : 'password'}
                  value={formData.pin}
                  maxLength={4}
                  onChange={(e) => setFormData(prev => ({ ...prev, pin: e.target.value.replace(/\D/g, '') }))}
                  placeholder={selected ? t('admin.mobOpOstaviPrazno') : t('admin.mobOpPin')}
                  style={{ flex: 1 }}
                />
                <Button type="button" icon={`pi ${showPin ? 'pi-eye-slash' : 'pi-eye'}`} className="p-button-text p-button-sm" onClick={() => setShowPin(p => !p)} />
              </div>
            </div>
            {selected && (
              <div style={{ margin: '1%', display: 'flex', width: '100%', alignItems: 'center' }}>
                <p style={{ margin: '0 5% 0 0', width: '150px' }}>{t('admin.nalogKorisnika')}:</p>
                <InputSwitch
                  style={{ display: 'flex', flexDirection: 'column-reverse', alignItems: 'start' }}
                  checked={selected.status == 1}
                  onChange={(e) => { handleToggleStatus(selected, e.value); setSelected(prev => ({ ...prev, status: e.value ? 1 : 0 })); }}
                />
              </div>
            )}
          </div>
        </Dialog>

        {pronadjiOpen && (
          <Dialog
            visible
            onHide={handlePronadjiClose}
            header={pronadjiRow ? `${t('admin.mobOpPronadji')} — ${pronadjiRow.FirstName} ${pronadjiRow.LastName}` : t('admin.mobOpPronadji')}
            style={{ width: '820px' }}
            breakpoints={{ '1200px': '90vw', '768px': '97vw' }}
            modal
            draggable={false}
            resizable={false}
            className="mob-pronadji-dialog"
            footer={
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                {/* Nov zahtev bez zatvaranja i ponovnog otvaranja dijaloga — vozač koji
                    je bio bez signala javi se iz drugog pokušaja. */}
                <Button onClick={() => pronadjiRow && handlePronadjiVozaca(pronadjiRow)}
                        disabled={pronadjiCekanje || pronadjiLoading}
                        icon="pi pi-refresh" label={t('admin.mobOpTraziPonovo')}
                        className="p-button p-button-raised" />
                <Button onClick={handlePronadjiClose} label={t('common.close')} className="p-button p-button-raised p-button-secondary" />
              </div>
            }
          >
            {pronadjiLoading && <Loading />}
            {!pronadjiLoading && (() => {
              /* Dve tačke, dva značenja. Sve se računa JEDNOM, pa i kartice i mapa
                 gledaju iste vrednosti — ranije je ista provera (`gps_enabled &&
                 lat != null`) stajala prepisana na pet mesta. */
              const stara = pronadjiPoslednja?.gps_enabled && pronadjiPoslednja.lat != null
                ? { lat: Number(pronadjiPoslednja.lat), lng: Number(pronadjiPoslednja.lng),
                    tacnost: Number(pronadjiPoslednja.tacnost_m) || null,
                    vreme: pronadjiPoslednja.DateEntered }
                : null;
              const zivo = pronadjiOdgovor?.gpsEnabled && pronadjiOdgovor.lat != null
                ? { lat: Number(pronadjiOdgovor.lat), lng: Number(pronadjiOdgovor.lng),
                    tacnost: Number(pronadjiOdgovor.tacnost) || null,
                    vreme: pronadjiOdgovor.vreme }
                : null;
              const tacke = [stara, zivo].filter(Boolean).map(x => [x.lat, x.lng]);

              /* Kartica je isti oblik za staru i za živu tačku — razlikuju se samo
                 bojom oznake i porukom kad tačke nema. */
              const kartica = (klasa, naslov, tacka, prazno) => (
                <div className={`mob-pronadji-karta ${klasa}`}>
                  <div className="mob-pronadji-naslov">
                    <span className="mob-pronadji-tocka" />
                    <span>{naslov}</span>
                  </div>
                  {tacka ? (
                    <>
                      <a className="mob-pronadji-koord" href={mapsLink(tacka.lat, tacka.lng)}
                         target="_blank" rel="noreferrer" title={t('admin.mobOpOtvoriGoogle')}>
                        {tacka.lat.toFixed(5)}, {tacka.lng.toFixed(5)}
                        <i className="pi pi-external-link" />
                      </a>
                      <div className="mob-pronadji-meta">
                        <span><i className="pi pi-clock" /> {formatVreme(tacka.vreme)}</span>
                        {tacka.tacnost != null && (
                          <span><i className="pi pi-compass" /> {t('admin.mobOpTacnost')} ±{Math.round(tacka.tacnost)} m</span>
                        )}
                      </div>
                    </>
                  ) : prazno}
                </div>
              );

              /* Upozorenja iz dijagnostike — samo ono što objašnjava izostanak odgovora.
                 Prazan spisak = sve izgleda u redu, pa se blok ni ne prikazuje. */
              const d = pronadjiDijag;
              const upozorenja = [];
              if (d) {
                if (!d.aktivan) upozorenja.push(t('admin.mobOpDijagPasivan'));
                if (d.zakljucanDo) upozorenja.push(t('admin.mobOpDijagZakljucan', { kada: formatVreme(d.zakljucanDo) }));
                if (!d.poslednjaAktivnost) upozorenja.push(t('admin.mobOpDijagNikadAktivna'));
                else if (Date.now() - new Date(d.poslednjaAktivnost).getTime() > 24 * 3600 * 1000) {
                  upozorenja.push(t('admin.mobOpDijagNeaktivna', { kada: formatVreme(d.poslednjaAktivnost) }));
                }
                if (d.istorija?.ukupno >= 3 && !d.istorija.odgovoreno) {
                  upozorenja.push(t('admin.mobOpDijagNikadOdgovara', { broj: d.istorija.ukupno }));
                }
              }
              const nemaOdgovora = !pronadjiOdgovor && (pronadjiDugo || pronadjiIsteklo);

              return (
                <div className="mob-pronadji">
                  {upozorenja.length > 0 && (
                    <div className="mob-pronadji-dijag">
                      {upozorenja.map((u, i) => (
                        <div key={i} className="mob-pronadji-dijag-red">
                          <i className="pi pi-exclamation-triangle" /> <span>{u}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {d?.istorija?.ukupno > 0 && d.istorija.odgovoreno > 0 && (
                    <div className="mob-pronadji-tiho mob-pronadji-istorija">
                      {t('admin.mobOpDijagIstorija', { odgovoreno: d.istorija.odgovoreno, ukupno: d.istorija.ukupno })}
                    </div>
                  )}
                  <div className="mob-pronadji-karte">
                    {kartica('mob-karta-stara', t('admin.mobOpPoslednjaLokacija'), stara, (
                      <span className="mob-pronadji-tiho">
                        {pronadjiPoslednja && !pronadjiPoslednja.gps_enabled
                          ? t('admin.mobOpGpsIskljucen')
                          : t('admin.mobOpNemaPoslednje')}
                      </span>
                    ))}

                    {kartica('mob-karta-zivo', t('admin.mobOpZivoJavljanje'), zivo, (
                      pronadjiOdgovor && !pronadjiOdgovor.gpsEnabled ? (
                        <span className="mob-pronadji-tiho">{t('admin.mobOpGpsIskljucen')}</span>
                      ) : pronadjiPush && !pronadjiPush.poslat ? (
                        /* Poruka nikad nije krenula. Najvažniji slučaj je zastareo token:
                           nalog izgleda uparen, a obaveštenja više ne stignu — to se iz
                           „čeka se odgovor“ nije moglo pogoditi. */
                        <>
                          <span className="mob-pronadji-lose">
                            <i className="pi pi-times-circle" /> {t('admin.mobOpPushNijeIsporucen')}
                          </span>
                          <span className="mob-pronadji-tiho">
                            {pronadjiPush.greska === 'DeviceNotRegistered'
                              ? t('admin.mobOpTokenNevazi')
                              : pronadjiPush.greska}
                          </span>
                        </>
                      ) : pronadjiPotvrda?.stanje === 'greska' ? (
                        /* Expo je primio poruku, ali je Google/Apple odbio isporuku —
                           tek potvrda (receipt) to otkriva, tiket je bio „ok". */
                        <>
                          <span className="mob-pronadji-lose">
                            <i className="pi pi-times-circle" /> {t('admin.mobOpPushNijeIsporucen')}
                          </span>
                          <span className="mob-pronadji-tiho">{razlogPusha(pronadjiPotvrda.greska)}</span>
                        </>
                      ) : pronadjiIsteklo ? (
                        <span className="mob-pronadji-ceka">
                          <i className="pi pi-exclamation-triangle" /> {t('admin.mobOpNemaOdgovora')}
                        </span>
                      ) : pronadjiCekanje ? (
                        <>
                          <span className="mob-pronadji-ceka">
                            <i className="pi pi-spin pi-spinner" /> {t('admin.mobOpCekaSeOdgovor')}
                          </span>
                          {/* Od 21.10.2026 (Play odbio lokaciju „Uvek"): otvorena aplikacija
                              odgovara sama, zatvorena prikaže obaveštenje i odgovara na dodir. */}
                          <span className="mob-pronadji-tiho">{t('admin.mobOpVozacNeVidi')}</span>
                          {pronadjiPotvrda?.stanje === 'isporuceno' && (
                            <span className="mob-pronadji-tiho">
                              <i className="pi pi-check" /> {t('admin.mobOpIsporucenoCeka')}
                            </span>
                          )}
                          {pronadjiDugo && (
                            <span className="mob-pronadji-ceka">
                              <i className="pi pi-exclamation-triangle" /> {t('admin.mobOpJosNemaOdgovora')}
                            </span>
                          )}
                        </>
                      ) : <span className="mob-pronadji-tiho">—</span>
                    ))}
                  </div>

                  {nemaOdgovora && (
                    <div className="mob-pronadji-proveri">
                      <div className="mob-pronadji-proveri-naslov">{t('admin.mobOpProveriNaslov')}</div>
                      <ol>
                        <li>{t('admin.mobOpProveri1')}</li>
                        <li>{t('admin.mobOpProveri2')}</li>
                        <li>{t('admin.mobOpProveri3')}</li>
                        <li>{t('admin.mobOpProveri4')}</li>
                      </ol>
                    </div>
                  )}

                  <div className="mob-pronadji-mapa">
                    {tacke.length ? (
                      <>
                        <MapContainer center={tacke[tacke.length - 1]} zoom={13}
                                      style={{ height: '100%', width: '100%' }}>
                          <TileLayer url={sloj.url} maxZoom={sloj.maxZoom} attribution={sloj.atribucija} />
                          <UokviriMarkere tacke={tacke} />
                          {/* Prsten tačnosti ide PRE oznake, da oznaka ostane iznad njega. */}
                          {stara?.tacnost ? (
                            <Circle center={[stara.lat, stara.lng]} radius={stara.tacnost}
                                    pathOptions={{ className: 'mob-krug-stara' }} />
                          ) : null}
                          {zivo?.tacnost ? (
                            <Circle center={[zivo.lat, zivo.lng]} radius={zivo.tacnost}
                                    pathOptions={{ className: 'mob-krug-zivo' }} />
                          ) : null}
                          {stara && (
                            <Marker position={[stara.lat, stara.lng]} icon={IKONA_POSLEDNJA} zIndexOffset={0}>
                              <MapTooltip direction="top" offset={[0, -34]}>
                                {t('admin.mobOpPoslednjaLokacija')} · {formatVreme(stara.vreme)}
                              </MapTooltip>
                            </Marker>
                          )}
                          {zivo && (
                            <Marker position={[zivo.lat, zivo.lng]} icon={IKONA_ZIVO} zIndexOffset={500}>
                              <MapTooltip direction="top" offset={[0, -40]}>
                                {t('admin.mobOpZivoJavljanje')} · {formatVreme(zivo.vreme)}
                              </MapTooltip>
                            </Marker>
                          )}
                        </MapContainer>

                        {/* Izbornik podloge lebdi NAD mapom, van MapContainer-a — isto
                            kao na GPS-u, pa mu ne treba `useMap()`. */}
                        <div className="mob-pronadji-slojevi">
                          {SLOJEVI.map(x => (
                            <button key={x.kljuc} type="button"
                                    className={x.kljuc === sloj.kljuc ? 'aktivan' : ''}
                                    onClick={() => setSloj(x)} title={t(x.opis)}>
                              <i className={x.ikona} /> {t(x.naziv)}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : (
                      /* Prazna mapa ISTE visine — bez nje dijalog poskoči kad živo
                         javljanje stigne, pa dugme „Zatvori“ odleti pod prst. */
                      <div className="mob-pronadji-prazno">
                        <i className="pi pi-map-marker" />
                        <span>{t('admin.mobOpNemaKoordinata')}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default MobilniOperateriTab;
